import { find, go, html, sel } from 'fxjs/es';
import { creatorPriceOfProduct } from '../../../ProductColorPrice/S/fs.js';
import { MpShopProductS } from '../../../MpShop/Product/S/Function/module/MpShopProductS.js';
import { NessLabsS } from '../Function/module/NessLabsS.js';
import { UtilS } from '../../../Util/S/Function/module/UtilS.js';
import $dataStr from 'fxdom/es/dataStr.js';
import { UtilTmplS } from '../../../Util/S/Tmpl/module/UtilTmplS.js';
import { commify } from '../../../Util/S/Function/util.js';
import { makeProductSellingTimeHtml } from '../../../MShop/App/ProductDetail/S/Tmpl/partials/productDetailForm.js';
import { NessLabsConstantS } from '../Constant/module/NessLabsConstantS.js';
import { MShopStudioPreDiscountProductS } from '../../../MShop/Studio/PreDiscountProduct/S/Function/module/MShopStudioPreDiscountProductS.js';

const { SALE_INFO_TYPE } = NessLabsConstantS;

/**
 * ### 상품 아이템
 * 항상 아이템을 생성할때 로딩상태로 그려낸 후에, loadProductListImages 로 합성 이미지를 받아와 교체함.
 * @param item
 * @returns {*}
 */
export const makeProductItem = (item) => {
  const component = 'maple-labs-product-list-item';
  const thumbnail = go(item.thumbnails.value, find(sel('is_thumb')));
  const is_not_yet_completed = thumbnail && thumbnail.is_not_yet;
  const origin_price = creatorPriceOfProduct(item);

  const { without_max_style = false } = item._.base_product;
  const { is_100, url_data, thumbnail_style } = MpShopProductS.calculateThumbnailImage(
    {
      ...item._.base_product,
      thumbnail,
    },
    {
      width: 600,
    },
  );

  const pre_discount_price = sel('_.pre_discount_product.pre_discount_price', item);
  const pre_discount_rate = pre_discount_price
    ? MShopStudioPreDiscountProductS.calcDiscountedRateByPercent(pre_discount_price, origin_price)
    : null;

  const need_tilde = NessLabsS.needTildePrice({
    maker_type: item._.base_product.maker_type,
    bp_option_ids: item._.selected_option_group.bp_option_ids,
  });
  const sale_info = productSaleInfo(item, is_not_yet_completed);
  const price = UtilS.commify(origin_price);

  const discount_price = () => {
    const _pre_discount_price = UtilS.commify(pre_discount_price);
    return html`
      <div class="${component}__discount-rate">${pre_discount_rate}%</div>
      <div class="${component}__price-number mp_currency" has_tilde="${need_tilde}">${price}</div>
      <div class="${component}__price-number--deprecated mp_currency">${_pre_discount_price}</div>
    `;
  };

  const no_discount_price = () =>
    html` <div class="${component}__price-number mp_currency" has_tilde="${need_tilde}">${price}</div>`;

  return html`
    <a
      href="/maplestore/products/${item.id}"
      class="${component} ${UtilS.htmlIf(!is_not_yet_completed, 'already_composite')}"
      data-fx-json="${$dataStr(item)}"
    >
      <div class="${component}__header">
        <div class="${component}__store">${item._.stores_product._.store.name}</div>
        <div class="${component}__badge ${NessLabsS.getProductBadge(item).type}">
          ${NessLabsS.getProductBadge(item).badge}
        </div>
        <h3 class="${component}__title">${item.name || item._.base_product.name}</h3>
        <div class="${component}__price">${pre_discount_rate ? discount_price() : no_discount_price()}</div>
      </div>
      <span
        class="${component}__body ${UtilS.htmlIf(is_not_yet_completed, 'is_not_yet')} mp-product-img-bg-wrap"
      >
        <img
          src="${MpShopProductS.setOnDemandJpegUrl(url_data)}"
          alt="${item.name || item._.base_product._.name}"
          class="${component}__product-img mp-product-img"
          data-product-id="${item.id}"
          style="${is_100 || without_max_style ? '' : thumbnail_style}"
        />
      </span>
      <div class="${component}__footer" data-sales_type="${sale_info.info}">
        ${makeProductItemButton(component, sale_info)}
      </div>
    </a>
  `;
};

export const makeProductItemButton = (component, sale_info) => {
  return html`<button class="${component}__button ${UtilTmplS.disableIf(sale_info.disabled)}">
    ${sale_info.content}
  </button>`;
};

export const productSaleInfo = (product, is_not_yet_completed) => {
  if (is_not_yet_completed) return { disabled: true, info: SALE_INFO_TYPE.READY, content: 'Rendering...' };

  const sales_info = NessLabsS.getProductSalesInfo(product);
  const {
    has_period_limit,
    show_quantity_limit,
    is_selling_product,
    is_before_sale,
    is_after_sale,
    is_sold_out,
    sell_start_at,
    sell_end_at,
    left_count,
    remain_time_infos,
  } = sales_info;

  // 1순위. [Sold Out] - 잔여 수량 0개 / 판매기간 종료 (disabled)
  if (is_sold_out || (has_period_limit && is_after_sale))
    return { disabled: true, info: SALE_INFO_TYPE.SOLD_OUT, content: 'Sold out' };
  // 2순위. [오픈까지] - 판매 시작 전 (disabled)
  if (has_period_limit && is_before_sale) {
    return {
      disabled: true,
      info: SALE_INFO_TYPE.PERIOD_LIMIT,
      content: ET('product_detail::noti::until_open', {
        start_at: sell_start_at,
        end_at: sell_end_at,
        remain_time: makeProductSellingTimeHtml(remain_time_infos),
      }),
    };
  }
  // 3순위. [수량 한정] - 수량 10개 미만일때
  if (left_count && show_quantity_limit && left_count < 10)
    return {
      disabled: false,
      info: SALE_INFO_TYPE.QUANTITY_LIMIT,
      content: ET('product_detail::noti::left', { left: commify(left_count) }),
    };
  // 4순위. [마감까지] - 마감 96시간 미만일때
  if (has_period_limit && is_selling_product && remain_time_infos.day <= 3 && remain_time_infos.hour < 24) {
    return {
      disabled: is_before_sale,
      info: SALE_INFO_TYPE.PERIOD_LIMIT,
      content: ET('product_detail::noti::until_close', {
        start_at: sell_start_at,
        end_at: sell_end_at,
        remain_time: makeProductSellingTimeHtml(remain_time_infos),
      }),
    };
  }
  // 5 순위. BUY NOW
  return { disabled: false, info: SALE_INFO_TYPE.DEFAULT, content: 'BUY NOW' };
};
